/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import { AppStartupInteractor } from "../../model/interactor";
import { Branding } from "../../model/branding";
import { RetryMechanism } from "../../model/retrymechanism";
import { RETRY_POLICY } from "../../model/types";
import { BrandingUiInfoKeys } from "../../model/entities";
import {
  extractServiceErrorMessage,
  formatRetryErrorMessage,
  localizedErrorFallback
} from "../../model/utility";
import { LocaleKeys } from "../../model/locale";
import {
  PARTNER_BRANDING_LOGO_WIDTH_1080P,
  PARTNER_BRANDING_LOGO_HEIGHT_1080P,
  PRIMARY_BRANDING_LOGO_WIDTH_1080P,
  PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
  TSRI_ID
} from "../../config";
import type { UnityRootState } from "../UnityAppStore";
import type { AppStartupState } from "./appStartup.types";
import { DeviceModel } from "../../model/device";
import { showAlertOverlay, dismissAlertOverlayIfOwned } from "../alertOverlay";
import {
  buildConnectionRetryActions,
  CONNECTION_WARMUP_RETRY_DELAYS_MS,
  getRetryNowSignal,
  isConnectionWarmupAttempt
} from "../connectionRetry";

const TAG: string = "appStartupSlice";

/**
 * Owner tag for this thunk's blocking overlay, so only its own retry takes it
 * down. See `dismissAlertOverlayIfOwned`.
 */
const ENDPOINTS_RETRY_OVERLAY_OWNER: string = "app-startup-endpoints-retry";

const INITIAL_STATE: AppStartupState = {
  isEndpointsFetching: false,
  isEndpointsFetched: false,
  endpointsError: "",
  isBrandingFetching: false,
  isBrandingFetched: false,
  brandingError: "",
  partnerBrandingImageUrl: "",
  primaryBrandingLogoUrl: "",
  isStringOverridesFetching: false,
  isStringOverridesFetched: false,
  stringOverridesError: "",
  stringOverrides: {}
};

/**
 * Fetches service endpoints from TSRI.
 * This is the first step in the application startup sequence.
 * The fetched endpoints are cached internally by the domain-lib
 * TsriServiceEndpointService and consumed by subsequent service calls.
 *
 * Uses CRITICAL retry — the overlay is shown on failure and the thunk
 * blocks until the endpoint resolves.
 */
export const fetchGlobalEndpoints = createAsyncThunk<
  void,
  { signal: AbortSignal },
  { rejectValue: string }
>(
  "appStartup/fetchGlobalEndpoints",
  async (
    { signal }: { signal: AbortSignal },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.d(TAG, "fetchGlobalEndpoints() started");
      const appStartupInteractor: AppStartupInteractor =
        new AppStartupInteractor();
      await RetryMechanism.callServiceWithRetry(
        () =>
          appStartupInteractor.fetchGlobalEndpoints({
            tsriId: TSRI_ID,
            originRequestId: DeviceModel.getDeviceUuid()
          }),
        [],
        {
          retryPolicy: RETRY_POLICY.CRITICAL,
          initialRetryDelaysMs: CONNECTION_WARMUP_RETRY_DELAYS_MS,
          getRetryNowSignal: getRetryNowSignal,
          onRetry: (error: unknown, attempt: number): void => {
            // Retry silently at [1s, 3s, 5s] before surfacing the overlay.
            if (isConnectionWarmupAttempt(attempt)) {
              return;
            }
            dispatch(
              showAlertOverlay({
                title: "Connection Error",
                message: formatRetryErrorMessage(error),
                actions: buildConnectionRetryActions(),
                owner: ENDPOINTS_RETRY_OVERLAY_OWNER
              })
            );
          },
          onSuccess: (): void => {
            dispatch(
              dismissAlertOverlayIfOwned({
                owner: ENDPOINTS_RETRY_OVERLAY_OWNER,
                dismissedByActionId: "retry-recovered"
              })
            );
          }
        },
        signal
      );
      Log.d(TAG, "fetchGlobalEndpoints() complete — endpoints fetched");
    } catch (error: unknown) {
      Log.e(TAG, "fetchGlobalEndpoints()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(LocaleKeys.APP_STARTUP_ENDPOINTS_ERROR_MESSAGE)
        )
      );
    }
  }
);

/**
 * Fetches MSO default branding UI bundle (pre-sign-in).
 * Must be called after fetchGlobalEndpoints so the branding service
 * endpoint is available in the TSRI cache.
 *
 * On success or failure the partner branding image URL is extracted
 * and stored in state for the splash screen, alongside the header-size
 * primary logo URL the Sign In header shows before sign-in.
 */
export const fetchMsoDefaultBranding = createAsyncThunk<
  { partnerBrandingImageUrl: string; primaryBrandingLogoUrl: string },
  void,
  { rejectValue: string }
>("appStartup/fetchMsoDefaultBranding", async (_: void) => {
  const appStartupInteractor: AppStartupInteractor = new AppStartupInteractor();

  // Initialize local fallback brandings early
  const locale: string = DeviceModel.getTVProperties().getLocale();
  const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
  await appStartupInteractor.initFallbackBrandings(locale, msoName);

  try {
    Log.d(TAG, "fetchMsoDefaultBranding() started");
    await appStartupInteractor.fetchMsoDefaultBrandingUiBundle();
    Log.d(TAG, "fetchMsoDefaultBranding() complete");
  } catch (error: unknown) {
    Log.e(TAG, "fetchMsoDefaultBranding()", error);
    // Non-fatal: fall through to extract whatever image URL is available
  }

  let partnerBrandingImageUrl: string =
    appStartupInteractor.getPartnerBrandingImageUrl(
      PARTNER_BRANDING_LOGO_WIDTH_1080P,
      PARTNER_BRANDING_LOGO_HEIGHT_1080P
    );

  // If API did not provide an image, use fallback branding
  if (partnerBrandingImageUrl === "") {
    Log.d(TAG, "fetchMsoDefaultBranding() — using fallback branding image");
    const fallbackImage = Branding.getFallbackBrandingUiInfoForKey(
      BrandingUiInfoKeys.IMAGE
    );
    if (
      fallbackImage !== null &&
      fallbackImage !== undefined &&
      typeof fallbackImage === "object"
    ) {
      const imageData = fallbackImage as {
        images?: Array<{ imageUrl: string }>;
      };
      if (
        imageData.images !== null &&
        imageData.images !== undefined &&
        imageData.images.length > 0
      ) {
        partnerBrandingImageUrl = imageData.images[0].imageUrl;
      }
    }
  }

  Log.d(
    TAG,
    "fetchMsoDefaultBranding() — partnerBrandingImageUrl=" +
      (partnerBrandingImageUrl.length > 0 ? "(set)" : "(empty)")
  );

  // Header-size logo from the same msoDefaultDeviceConfig response. No local
  // fallback here: the Sign In header falls back to its baked-in logo itself.
  const primaryBrandingLogoUrl: string =
    appStartupInteractor.getPartnerBrandingImageUrl(
      PRIMARY_BRANDING_LOGO_WIDTH_1080P,
      PRIMARY_BRANDING_LOGO_HEIGHT_1080P
    );
  Log.d(
    TAG,
    "fetchMsoDefaultBranding() — primaryBrandingLogoUrl=" +
      (primaryBrandingLogoUrl.length > 0 ? "(set)" : "(empty)")
  );

  return { partnerBrandingImageUrl, primaryBrandingLogoUrl };
});

/**
 * Fetches the MSO default (pre-sign-in) string overrides.
 *
 * Sibling of {@link fetchMsoDefaultBranding} — same service
 * (device-configuration-service), same pre-sign-in msoName-only request
 * shape. Fail-open: a fetch failure is logged and swallowed rather than
 * rejecting, so `isStringOverridesFetched` still flips once this settles
 * and Step 3 (initializeSignIn) is not blocked by a string-overrides
 * outage — same non-fatal treatment as `fetchMsoDefaultBranding`.
 */
export const fetchMsoDefaultStringOverrides = createAsyncThunk<
  Record<string, string>,
  void,
  { rejectValue: string }
>("appStartup/fetchMsoDefaultStringOverrides", async (_: void) => {
  const appStartupInteractor: AppStartupInteractor = new AppStartupInteractor();

  let overrides: Record<string, string> = {};
  try {
    Log.d(TAG, "fetchMsoDefaultStringOverrides() started");
    const result = await appStartupInteractor.fetchMsoDefaultStringOverrides();
    overrides = result.overrides;
    Log.d(TAG, "fetchMsoDefaultStringOverrides() complete");
  } catch (error: unknown) {
    Log.e(TAG, "fetchMsoDefaultStringOverrides()", error);
    // Non-fatal: fall through with whatever overrides (none) are available.
  }

  return overrides;
});

const appStartupSlice = createSlice({
  name: "appStartup",
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGlobalEndpoints.pending, (state) => {
      state.isEndpointsFetching = true;
      state.isEndpointsFetched = false;
      state.endpointsError = "";
    });
    builder.addCase(fetchGlobalEndpoints.fulfilled, (state) => {
      state.isEndpointsFetching = false;
      state.isEndpointsFetched = true;
    });
    builder.addCase(fetchGlobalEndpoints.rejected, (state, action) => {
      state.isEndpointsFetching = false;
      state.isEndpointsFetched = false;
      state.endpointsError =
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : localizedErrorFallback(
              LocaleKeys.APP_STARTUP_ENDPOINTS_ERROR_MESSAGE
            );
    });
    builder.addCase(fetchMsoDefaultBranding.pending, (state) => {
      state.isBrandingFetching = true;
      state.isBrandingFetched = false;
      state.brandingError = "";
    });
    builder.addCase(fetchMsoDefaultBranding.fulfilled, (state, action) => {
      state.isBrandingFetching = false;
      state.isBrandingFetched = true;
      state.partnerBrandingImageUrl = action.payload.partnerBrandingImageUrl;
      state.primaryBrandingLogoUrl = action.payload.primaryBrandingLogoUrl;
    });
    builder.addCase(fetchMsoDefaultBranding.rejected, (state, action) => {
      state.isBrandingFetching = false;
      state.isBrandingFetched = true;
      state.brandingError =
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : "MSO default branding fetch failed";
    });
    builder.addCase(fetchMsoDefaultStringOverrides.pending, (state) => {
      state.isStringOverridesFetching = true;
      state.isStringOverridesFetched = false;
      state.stringOverridesError = "";
    });
    builder.addCase(
      fetchMsoDefaultStringOverrides.fulfilled,
      (state, action) => {
        state.isStringOverridesFetching = false;
        state.isStringOverridesFetched = true;
        state.stringOverrides = action.payload;
      }
    );
    builder.addCase(
      fetchMsoDefaultStringOverrides.rejected,
      (state, action) => {
        state.isStringOverridesFetching = false;
        state.isStringOverridesFetched = true;
        state.stringOverridesError =
          action.payload !== undefined && action.payload !== null
            ? action.payload
            : "MSO default string overrides fetch failed";
      }
    );
  }
});

export const selectIsEndpointsFetching = (state: UnityRootState): boolean =>
  state.appStartup.isEndpointsFetching;

export const selectIsEndpointsFetched = (state: UnityRootState): boolean =>
  state.appStartup.isEndpointsFetched;

export const selectEndpointsError = (state: UnityRootState): string =>
  state.appStartup.endpointsError;

export const selectIsBrandingFetching = (state: UnityRootState): boolean =>
  state.appStartup.isBrandingFetching;

export const selectIsBrandingFetched = (state: UnityRootState): boolean =>
  state.appStartup.isBrandingFetched;

export const selectBrandingError = (state: UnityRootState): string =>
  state.appStartup.brandingError;

export const selectPartnerBrandingImageUrl = (state: UnityRootState): string =>
  state.appStartup.partnerBrandingImageUrl;

export const selectPrimaryBrandingLogoUrl = (state: UnityRootState): string =>
  state.appStartup.primaryBrandingLogoUrl;

export const selectIsStringOverridesFetching = (
  state: UnityRootState
): boolean => state.appStartup.isStringOverridesFetching;

export const selectIsStringOverridesFetched = (
  state: UnityRootState
): boolean => state.appStartup.isStringOverridesFetched;

export const selectStringOverridesError = (state: UnityRootState): string =>
  state.appStartup.stringOverridesError;

export const selectStringOverrides = (
  state: UnityRootState
): Record<string, string> => state.appStartup.stringOverrides;

export default appStartupSlice.reducer;
